/**
 * One list of Geotab install types for StatsView and the Report Builder.
 *
 * The portal drops self-powered installs (BeSol and battery-powered) from the work charts
 * and drops motion-based installs from towing. The builder's Harness filter used to keep
 * its own copy of the names and of the three older spellings. Both now read this file.
 */

export const GEOTAB_INSTALL_CAN = 'CAN-connected';
export const GEOTAB_INSTALL_3WIRE = '3-wire';
export const GEOTAB_INSTALL_MOTION = 'Motion-based';
export const GEOTAB_INSTALL_UNKNOWN = 'Unknown';
export const GEOTAB_INSTALL_BESOL = 'BeSol / No Harness';
export const GEOTAB_INSTALL_BATTERY_POWERED = 'Battery-powered';

/** Tiers the hysteresis worker publishes. Self-powered types are assigned a different way. */
export const CANONICAL_GEOTAB_INSTALL_TYPES = [
  GEOTAB_INSTALL_CAN,
  GEOTAB_INSTALL_3WIRE,
  GEOTAB_INSTALL_MOTION,
  GEOTAB_INSTALL_UNKNOWN,
];

/** Older spellings a device record may still carry. Same map the hysteresis worker uses. */
export const LEGACY_TO_CANONICAL = {
  'J1939 / OBD with CAN': GEOTAB_INSTALL_CAN,
  '3 Wire': GEOTAB_INSTALL_3WIRE,
  'J1939 / OBD with no CAN': GEOTAB_INSTALL_MOTION,
};

/** Installs with no engine-hours signal. StatsView leaves these out of the work charts. */
export const SELF_POWERED_TYPES = [GEOTAB_INSTALL_BESOL, GEOTAB_INSTALL_BATTERY_POWERED];

/** Every type a filter can offer, canonical names only. */
export const ALL_INSTALL_TYPES = [...CANONICAL_GEOTAB_INSTALL_TYPES, ...SELF_POWERED_TYPES];

// BeWhere serial in the Geotab Device.comment.
// BeSol serials are BTSAE-prefixed, BeSol+ serials are BSPAC-prefixed.
const BEWHERE_SERIAL_REGEX = /(?:btsae|bspac)[0-9]{14}/;

/**
 * @param {string} comment Geotab device comment
 * @return {string|undefined}
 */
export function extractBeWhereSerialFromGeotabComment(comment) {
  if (!comment || typeof comment !== 'string') {
    return undefined;
  }
  const m = comment.toLowerCase().match(BEWHERE_SERIAL_REGEX);
  return m ? m[0].toUpperCase() : undefined;
}

/**
 * The device record StatsView already looks up: the vehicle's own connection first,
 * then any connection that has the device.
 * @param {Object} activeDevices
 * @param {Object} vehicleDocument
 * @return {Object|undefined}
 */
function geotabDeviceOf(activeDevices, vehicleDocument) {
  const deviceId = vehicleDocument && vehicleDocument.device && vehicleDocument.device.id;
  if (deviceId == null || deviceId === '' || !activeDevices || !activeDevices.geotab) return undefined;
  const byConn = activeDevices.geotab;
  if (vehicleDocument.connectionid && byConn[vehicleDocument.connectionid] && byConn[vehicleDocument.connectionid][deviceId]) {
    return byConn[vehicleDocument.connectionid][deviceId];
  }
  for (const connMap of Object.values(byConn)) {
    if (connMap && connMap[deviceId]) return connMap[deviceId];
  }
  return undefined;
}

/**
 * A stored install type as its canonical name.
 * "Unknown" and a blank stay blank for the builder's Harness fact, which folds them
 * into "No harness type" so they are not read as "BeSol / No Harness".
 * @param {*} value
 * @param {boolean} [foldUnknown]
 * @return {string}
 */
export function canonicalInstallType(value, foldUnknown = false) {
  const k = String(value || '').trim();
  if (!k) return '';
  const mapped = LEGACY_TO_CANONICAL[k] || k;
  if (foldUnknown && mapped === GEOTAB_INSTALL_UNKNOWN) return '';
  return mapped;
}

/**
 * The install type of one vehicle.
 * The published map wins. When it has no entry, BeSol is read from the Geotab comment
 * and a battery install from a serial that starts with B1, which is what the charts
 * already did when the map was missing.
 * @param {string} sn vehicle serial
 * @param {Object} vehicle vehicle record (serialNumber, device, connectionid)
 * @param {Object} activeDevices
 * @param {Object} installTypesBySerial serial to install type
 * @return {string} a canonical type, or '' when none resolves
 */
export function installTypeOf(sn, vehicle, activeDevices, installTypesBySerial) {
  const doc = vehicle ? {...vehicle} : {};
  if (sn) doc.serialNumber = sn;
  const mapped = mappedType(doc, installTypesBySerial);
  // A published type wins, including a blank canonical name, so the fallbacks do not second-guess it.
  if (mapped !== undefined) return mapped;
  if (checkIfDeviceIsBesol(doc, activeDevices, installTypesBySerial)) return GEOTAB_INSTALL_BESOL;
  if (checkIfDeviceIsBatteryPowered(doc, activeDevices, installTypesBySerial)) return GEOTAB_INSTALL_BATTERY_POWERED;
  return '';
}

/**
 * The published map, when it has an entry for this serial. Undefined when it does not,
 * so the comment and serial fallbacks still run.
 * @param {Object} vehicleDocument
 * @param {Object} installTypesBySerial
 * @return {string|undefined}
 */
function mappedType(vehicleDocument, installTypesBySerial) {
  const sn = vehicleDocument && vehicleDocument.serialNumber;
  if (sn && installTypesBySerial && Object.prototype.hasOwnProperty.call(installTypesBySerial, sn)) {
    return canonicalInstallType(installTypesBySerial[sn]);
  }
  return undefined;
}

/**
 * @param {Object} vehicleDocument
 * @param {Object} activeDevices
 * @param {Object} [installTypesBySerial]
 * @return {boolean}
 */
export function checkIfDeviceIsBesol(vehicleDocument, activeDevices, installTypesBySerial = null) {
  const mapped = mappedType(vehicleDocument, installTypesBySerial);
  if (mapped !== undefined) return mapped === GEOTAB_INSTALL_BESOL;
  if (!vehicleDocument?.device?.serialNumber || !activeDevices?.geotab) return false;
  const device = geotabDeviceOf(activeDevices, vehicleDocument);
  return extractBeWhereSerialFromGeotabComment(device && device.comment) !== undefined;
}

/**
 * @param {Object} vehicleDocument
 * @param {Object} activeDevices
 * @param {Object} [installTypesBySerial]
 * @return {boolean}
 */
export function checkIfDeviceIsBatteryPowered(vehicleDocument, activeDevices, installTypesBySerial = null) {
  const mapped = mappedType(vehicleDocument, installTypesBySerial);
  if (mapped !== undefined) return mapped === GEOTAB_INSTALL_BATTERY_POWERED;
  if (!vehicleDocument?.device?.serialNumber || !activeDevices?.geotab) return false;
  const geotabSerial = vehicleDocument.device.serialNumber;
  return typeof geotabSerial === 'string' && geotabSerial.startsWith('B1');
}

/**
 * BeSol or battery-powered Geotab install. Same exclusion the work charts have always applied.
 * The two checks stay independent: a device can match the comment and the B1 serial at once,
 * and either one is enough, which is what the old pair of functions did.
 * @param {Object} vehicleDocument
 * @param {Object} activeDevices
 * @param {Object} [installTypesBySerial]
 * @return {boolean}
 */
export function checkIfDeviceIsSelfPoweredGeotabInstall(vehicleDocument, activeDevices, installTypesBySerial = null) {
  return (
    checkIfDeviceIsBesol(vehicleDocument, activeDevices, installTypesBySerial) ||
    checkIfDeviceIsBatteryPowered(vehicleDocument, activeDevices, installTypesBySerial)
  );
}

/**
 * What a new Report Builder panel counts when the author has not picked install types.
 * StatsView's work charts drop the two self-powered types. Towing also drops motion-based,
 * because those installs do not report a tow signal.
 * @param {{towing?: boolean}=} options
 * @return {Array<string>}
 */
export function defaultInstallTypeSelection(options = {}) {
  return ALL_INSTALL_TYPES.filter((type) => {
    if (SELF_POWERED_TYPES.includes(type)) return false;
    if (options.towing && type === GEOTAB_INSTALL_MOTION) return false;
    return true;
  });
}
